'use client';

import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams, useRouter } from 'next/navigation';
import {
  useGetOrderByIdQuery,
  setOrderItemData,
  setTicketsSelected,
  clearReservation,
  useGetEventCardPromotionsQuery,
  useCancelCheckoutSessionMutation,
} from '../../../store';
import { setSelectedEvent } from '../../../store/slices/events';
import Ticket from '../../../components/Ticket';
import TicketDTO from '../../../models/ticket';
import EventDTO, { EventPublicStatus } from '../../../models/event';
import CarrouselCards from '../../../components/atoms/CarrouselCards/CarrouselCards';
import CardsLottie from '../../../components/CardsLottie';
import getScreenSize from '../../../utils/getScreenSize';
import useSiteBusiness from '../../../hooks/useSiteBusiness';
import CouponHeadsUp from '../../../components/CouponHeadsUp';
import { PendingOrderStatus } from '../../../models/order';
import useEventPixel from '../../../hooks/useEventPixel';
import { trackViewContent } from '../../../utils/metaPixelTracking';
import {
  ABANDONED_CART_STEP_PARAM,
  ABANDONED_CART_TOKEN_PARAM,
  storeAbandonedCartToken,
} from '../../../utils/abandonedCartTracking';
import { IconEnjoy3 } from '../../../components/icons';
import SimplifiedSalePage from '../../../views/SimplifiedSalePage';
import BackgroundBannerImage from '../../../components/BackgroundBannerImage';
import { useAppConfig } from '../../../contexts/AppConfigContext';
import { writeStoredReferral } from '../../../utils/eventReferral';
import isEventComingSoon from '../../../utils/isEventComingSoon';
import useCheckoutEvents from '../../../hooks/useCheckoutEvents';
import { storeAttributionFromParams } from '../../../utils/checkoutAttribution';
import { storeLinkEmail } from '../../../utils/checkoutLinkEmail';
import { trackGa4Event } from '../../../utils/ga4Tracking';
import useCheckoutVersion from '../../../hooks/useCheckoutVersion';
import useCheckoutSession from '../../../hooks/useCheckoutSession';
import isCheckoutV2Eligible from '../../../utils/checkoutV2Eligibility';
import ResumeCard from '../../../components/checkout-v2/ResumeCard';
import { PublicCardPromotionDTO } from '../../../types/CardPromotion';
import Button from '../../../components/atoms/Button';
import TimerChip from '../../../components/atoms/TimerChip';

interface EventPageClientProps {
  event: EventDTO;
  relatedEvents: EventDTO[];
  minTicketPrice: number | undefined;
  eventContent: React.ReactNode;
  onNavigate?: (path: string, options?: { replace?: boolean }) => void;
  // Las cuotas que ya trajo el servidor: el logo aparece con la ficha.
  initialCardPromotions?: PublicCardPromotionDTO[];
}

export default function EventPageClient({
  event,
  relatedEvents,
  minTicketPrice,
  eventContent,
  onNavigate,
  initialCardPromotions,
}: EventPageClientProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const dispatch = useDispatch();
  const ticketRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const [isCardOnScreen, setIsCardOnScreen] = useState(false);
  const [isTicketOnScreen, setIsTicketOnScreen] = useState(false);
  // Se mide después de montar: el servidor no sabe el ancho, y leerlo en el
  // primer render hacía que la ficha no coincidiera con el HTML del servidor y
  // React la volviera a armar entera al cargar.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const measure = () => setIsMobile(['xs', 'sm', 'md'].includes(getScreenSize()));
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const orderId = searchParams?.get('orderId') ?? null;
  const { paymentsEnabled } = useAppConfig();
  const { data: order } = useGetOrderByIdQuery(
    { orderId: orderId || '' },
    { skip: !orderId },
  );
  const { business, isProducerSite } = useSiteBusiness();

  const hasNoCodesAvailable = event.codes_available === 0;
  const displayEvent: EventDTO = hasNoCodesAvailable
    ? { ...event, public_status: EventPublicStatus.NoTicketsAvailable }
    : event;
  // Con la venta todavía sin abrir, el checkout rápido embebido tampoco se muestra:
  // va la card de "Próximamente" como en cualquier otro evento.
  useEventPixel(event);
  const checkoutVersion = useCheckoutVersion();
  const isV2 = checkoutVersion === 2 && isCheckoutV2Eligible(event);
  // Con el checkout nuevo, un evento de compra rápida muestra el botón de
  // compra como cualquier otro: el formulario embebido es del checkout de siempre.
  const showSimplifiedCheckout = event.superfast_checkout && !isV2 && !hasNoCodesAvailable && !isEventComingSoon(event);
  const { track } = useCheckoutEvents(event, true, isV2 ? 2 : 1);
  const { session: pendingSession, forget: forgetSession } = useCheckoutSession(isV2 ? event : null);
  const [cancelSession] = useCancelCheckoutSessionMutation();
  const [openingCheckout, setOpeningCheckout] = useState(false);
  const [resumeDismissed, setResumeDismissed] = useState(false);
  // Volver con Atrás restaura la página tal como quedó: sin esto el botón
  // seguía cargando.
  useEffect(() => {
    const reset = () => setOpeningCheckout(false);
    window.addEventListener('pageshow', reset);
    return () => window.removeEventListener('pageshow', reset);
  }, []);
  const { data: cardPromotions } = useGetEventCardPromotionsQuery(
    { businessId: event.business_id, eventId: event.id },
    { skip: !isV2 },
  );
  const ticketsPath = `/events/${event.slug}/orders/new/entradas`;
  const v2Promotions = (cardPromotions ?? initialCardPromotions ?? []).filter((promo) => promo.is_active && promo.installments.length > 0);
  const resumable = !!pendingSession && (pendingSession.status === 'open' || pendingSession.status === 'ordered')
    && new Date(pendingSession.expires_at).getTime() > Date.now();

  // Meta Pixel: ViewContent for the public event page. The embedded
  // SimplifiedSalePage intentionally skips its own (eventpage guard), so
  // without this the event page would only ever send PageView.
  useEffect(() => {
    trackViewContent({
      content_name: event.name,
      content_type: 'product',
      content_ids: [event.id],
    });
  }, [event.id]);

  // Primer paso del recorrido de compra, para medir de la ficha a la entrada.
  useEffect(() => {
    track('event_view');
    trackGa4Event('view_item', {
      currency: event.currency,
      items: [{ item_id: event.id, item_name: event.name }],
    });
  }, [event.id, track]);

  // Los links de anuncios e historias aterrizan acá con UTM, fbclid o gclid.
  useEffect(() => {
    storeAttributionFromParams(new URLSearchParams(searchParams?.toString() ?? ''));
    storeLinkEmail(searchParams?.toString() ?? '');
  }, [searchParams]);

  useEffect(() => {
    dispatch(setSelectedEvent(displayEvent));
    // Preload the SPA bundle so the order-flow transition is instant
    import('../../../LegacySPA');
  }, [displayEvent, dispatch]);

  useEffect(() => {
    const ref = searchParams?.get('ref')?.trim() ?? null;
    if (event && ref) {
      writeStoredReferral(event.id, ref);
    }
  }, [event, searchParams]);

  // Aterrizaje del mail de carrito abandonado. El token se guarda acá porque
  // esta es la página a la que apunta el CTA; el checkout lo lee después,
  // incluso en una visita posterior.
  useEffect(() => {
    const cartToken = searchParams?.get(ABANDONED_CART_TOKEN_PARAM) ?? null;
    if (event && cartToken) {
      storeAbandonedCartToken(
        event.id,
        cartToken,
        searchParams?.get(ABANDONED_CART_STEP_PARAM) ?? null,
      );
    }
  }, [event, searchParams]);

  useEffect(() => {
    if (event && !event.has_numerated_seats) {
      dispatch(clearReservation());
    }
  }, [dispatch, event]);

  useEffect(() => {
    if (
      orderId
      && order
      && (order.pending_status === PendingOrderStatus.no_reminder
        || order.pending_status === PendingOrderStatus.first_reminder
        || order.pending_status === PendingOrderStatus.second_reminder
        || order.pending_status === PendingOrderStatus.third_reminder)
    ) {
      dispatch(
        setTicketsSelected(
          order.items.reduce(
            (acc, item) => {
              const existingTicket = acc.find(
                (t) => t.ticket.id === item.ticket.id,
              );
              if (existingTicket) {
                existingTicket.quantity += 1;
                return acc;
              }
              return [...acc, { ticket: item.ticket, quantity: 1 }];
            },
            [] as { ticket: TicketDTO; quantity: number }[],
          ),
        ),
      );

      dispatch(
        setOrderItemData(
          order.items.map((item) => ({
            ...item,
            ticket_id: item.ticket.id,
            ticket_extra: item.ticket.is_extra,
            seat_name: item?.event_seat?.name || '',
            seat_id: item?.event_seat?.id || '',
          })),
        ),
      );

      const checkoutLink = order.payments?.[0]?.checkout_link;
      const linkQuery = paymentsEnabled && checkoutLink
        ? `&link=${checkoutLink}`
        : '';
      const path = `/events/${event.slug}/orders/new/checkout?email=${order.email}${linkQuery}`;
      if (onNavigate) {
        onNavigate(path);
      } else {
        router.push(path);
      }
    }
  }, [orderId, order, dispatch, event, router, onNavigate, paymentsEnabled]);

  useEffect(() => {
    const setupObservers = () => {
      const ticketObserver = new IntersectionObserver(
        ([entry]) => {
          setIsTicketOnScreen(entry.isIntersecting);
        },
        {
          root: null,
          threshold: event?.superfast_checkout ? 0.1 : 0.5,
        },
      );

      const cardsObserver = new IntersectionObserver(
        ([entry]) => {
          setIsCardOnScreen(entry.isIntersecting);
        },
        {
          root: null,
          threshold: 0.2,
        },
      );

      if (ticketRef.current) {
        ticketObserver.observe(ticketRef.current);
      }

      if (cardsRef.current) {
        cardsObserver.observe(cardsRef.current);
      }

      return () => {
        ticketObserver.disconnect();
        cardsObserver.disconnect();
      };
    };

    const timeoutId = setTimeout(setupObservers, 100);
    return () => clearTimeout(timeoutId);
  }, [event]);

  const goTo = (path: string) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      router.push(path);
    }
  };

  // Elegir entradas es una página del SPA: se baja su código mientras se mira
  // la ficha, para que "Comprar entradas" abra al toque y sin el logo animado.
  useEffect(() => {
    if (!isV2 || onNavigate) {
      return undefined;
    }
    const timer = setTimeout(() => {
      router.prefetch(ticketsPath);
      import('../../../LegacySPA');
      import('../../../views/checkout-v2/EntradasPage');
    }, 1500);
    return () => clearTimeout(timer);
  }, [isV2, onNavigate, router, ticketsPath]);

  // Links viejos (reserva vencida) que abrían la hoja de entradas con ?comprar=1.
  // Reemplaza en el historial (con push, Atrás volvía a ?comprar=1 y de ahí otra
  // vez a Entradas) y conserva el resto de los parámetros (UTM, cupón).
  useEffect(() => {
    if (!isV2 || searchParams?.get('comprar') !== '1') {
      return;
    }
    const rest = new URLSearchParams(searchParams.toString());
    rest.delete('comprar');
    const path = rest.toString() ? `${ticketsPath}?${rest.toString()}` : ticketsPath;
    if (onNavigate) {
      onNavigate(path, { replace: true });
    } else {
      router.replace(path);
    }
  }, [isV2, searchParams]);

  const handleClickBuyTickets = () => {
    if (isV2) {
      setOpeningCheckout(true);
    }
    if (isV2 && !event.has_numerated_seats) {
      goTo(ticketsPath);
      return;
    }
    if (showSimplifiedCheckout) {
      ticketRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      const basePath = `/events/${event.slug}/orders/new/${event.has_numerated_seats ? 'seats' : 'tickets'}`;
      const search = searchParams?.toString();
      const path = search ? `${basePath}?${search}` : basePath;
      if (onNavigate) {
        onNavigate(path);
      } else {
        router.push(path);
      }
    }
  };

  // "Tenés una compra sin terminar" va en el lugar del botón de compra (el
  // flotante tapaba el banner, el nombre y el precio, y dejaba dos botones
  // para lo mismo). Cerrarlo vuelve a mostrar el botón.
  const resumeCheckout = () => {
    if (!pendingSession) {
      return;
    }
    track('resume_accepted');
    goTo(`/events/${event.slug}/orders/new/${pendingSession.contact ? 'pago' : 'datos'}`);
  };
  const resumeCard = isV2 && resumable && pendingSession && !resumeDismissed ? (
    <ResumeCard
      onDismiss={() => setResumeDismissed(true)}
      session={pendingSession}
      onResume={resumeCheckout}
      // Con el CVU pedido no se empieza otra: el backend no la cancela (la
      // transferencia puede llegar) y quedarían dos compras abiertas.
      onStartOver={pendingSession.order?.open_payment ? undefined : () => {
        // "Empezar de nuevo" suelta la reserva enseguida, no cuando vence.
        cancelSession({ businessId: event.business_id, eventId: event.id, token: pendingSession.token }).catch(() => undefined);
        forgetSession();
      }}
    />
  ) : null;

  return (
    <>
      <BackgroundBannerImage event={event} />
      <CouponHeadsUp />

      {/* Grid with content + ticket in document flow so sticky works (top-32 = below header) */}
      <div className="grid grid-cols-1 lg:grid-cols-11 gap-12 my-6 lg:my-12 lg:px-8 z-10">
        {eventContent}
        <div className="relative hidden lg:block lg:col-span-5 xl:col-span-4">
          {!showSimplifiedCheckout && (
            <div ref={ticketRef} className="sticky top-32 z-10">
              {resumeCard ?? (
                <Ticket
                  price={minTicketPrice}
                  onClick={handleClickBuyTickets}
                  event={displayEvent}
                  promotions={isV2 ? v2Promotions : []}
                  loading={openingCheckout}
                />
              )}
            </div>
          )}
          {showSimplifiedCheckout && (
            <div>
              <SimplifiedSalePage eventpage />
            </div>
          )}
        </div>
      </div>

      {/* Mobile ticket section */}
      {isMobile && (
        <>
          <div
            className={`fixed bottom-1 left-0 right-0 z-50 mx-4 ${
              isTicketOnScreen || isCardOnScreen ? 'hidden' : 'block'
            }`}
          >
            {resumeCard && pendingSession ? (
              // La barra fija es chica: solo seguir con la compra y el reloj.
              <div className="flex items-center gap-3 rounded-3xl border border-border-secondary bg-surface-base p-3 shadow-lg">
                <TimerChip expiresAt={pendingSession.expires_at} label="Entradas reservadas" />
                <div className="flex-1">
                  <Button variant="primary" onClick={resumeCheckout}>Seguir con la compra</Button>
                </div>
              </div>
            ) : (
              <Ticket
                price={minTicketPrice}
                onClick={handleClickBuyTickets}
                event={displayEvent}
                promotions={isV2 ? v2Promotions : []}
                loading={openingCheckout}
              />
            )}
          </div>
          <div ref={ticketRef} className="mb-6">
            {!showSimplifiedCheckout && (resumeCard ?? (
              <Ticket
                price={minTicketPrice}
                onClick={handleClickBuyTickets}
                event={displayEvent}
                promotions={isV2 ? v2Promotions : []}
                loading={openingCheckout}
              />
            ))}
            {showSimplifiedCheckout && <SimplifiedSalePage eventpage />}
          </div>
        </>
      )}

      {/* Bottom section: lottie + related events */}
      <div className="flex flex-col gap-8 lg:gap-12 lg:px-8 z-10 mb-12">
        {!business?.is_white_label && <CardsLottie />}
        {!isProducerSite && relatedEvents.length > 0 && (
          <aside data-nosnippet="">
            <div className="flex flex-row items-center gap-4">
              <span className="text-xl-semibold lg:display-lg-semibold text-text-primary">
                Te puede interesar
              </span>
              <IconEnjoy3 className="w-10 h-10" />
            </div>
            <div ref={cardsRef}>
              <CarrouselCards cards={relatedEvents} />
            </div>
          </aside>
        )}
      </div>
    </>
  );
}
